import { useState } from "react";
import { Divider, Stack, useTheme } from "@mui/material";
import { AnchoredPopover, FoodsCheckbox, FoodsIconButton, FoodsRadioList, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    AUDIT_LOG_COLUMN_IDS,
    AUDIT_LOG_DENSITIES,
    visibleAuditLogColumns,
    type AuditLogColumnId,
    type AuditLogDensity,
} from "./auditLogTablePreferences";
import type { AuditLogTablePreferences } from "./useAuditLogTablePreferences";

type ColumnOption = {
    id: AuditLogColumnId;
    name: string;
};

type DensityOption = {
    id: AuditLogDensity;
    name: string;
};

export const AuditLogTableMenu = (props: AuditLogTablePreferences) => {
    const { columnVisibility, setVisibleColumns, density, setDensity } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const columnLabels: Record<AuditLogColumnId, string> = {
        occurredAt: t("settings.audit_log.column.occurred_at_utc", "Time (UTC)"),
        actorLabel: t("settings.audit_log.column.actor", "Actor"),
        entityLabel: t("settings.audit_log.column.affected_object", "Affected object"),
        rootLabel: t("settings.audit_log.column.scope", "Scope"),
        ip: t("settings.audit_log.column.ip", "IP"),
    };

    const densityLabels: Record<AuditLogDensity, string> = {
        compact: t("settings.audit_log.density.compact", "Compact"),
        comfortable: t("settings.audit_log.density.comfortable", "Comfortable"),
    };

    const columnOptions: ColumnOption[] = AUDIT_LOG_COLUMN_IDS.map((id) => ({ id, name: columnLabels[id] }));
    const selectedColumns = columnOptions.filter((option) =>
        visibleAuditLogColumns(columnVisibility).includes(option.id),
    );
    const densityOptions: DensityOption[] = AUDIT_LOG_DENSITIES.map((id) => ({ id, name: densityLabels[id] }));

    return (
        <>
            <FoodsIconButton
                icon="columns"
                size="s"
                tooltip={{ text: t("settings.audit_log.table_menu", "Columns and density") }}
                buttonProps={{ onClick: (event) => setAnchor(event.currentTarget) }}
            />
            <AnchoredPopover
                open={anchor !== null}
                anchor={anchor}
                onClose={() => setAnchor(null)}
                anchorPosition="rightside"
            >
                <Stack gap={theme.spacing(2)} padding={theme.spacing(2)} minWidth={theme.spacing(28)}>
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="small-regular" color="text.secondary">
                            {t("settings.audit_log.table_menu.columns", "Columns")}
                        </Typography>
                        <FoodsCheckbox<ColumnOption>
                            selectedItems={selectedColumns}
                            values={columnOptions}
                            onChange={(selected) => setVisibleColumns(selected.map((option) => option.id))}
                            getId={(option) => option.id}
                            getName={(option) => option.name}
                        />
                    </Stack>

                    <Divider />

                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="small-regular" color="text.secondary">
                            {t("settings.audit_log.table_menu.density", "Row density")}
                        </Typography>
                        <FoodsRadioList<DensityOption>
                            selectedItem={densityOptions.find((option) => option.id === density)}
                            values={densityOptions}
                            onChange={(option) => setDensity(option.id)}
                            getId={(option) => option.id}
                            getName={(option) => option.name}
                        />
                    </Stack>
                </Stack>
            </AnchoredPopover>
        </>
    );
};
